/**
 * Notifications API
 * RTK Query endpoints for notification management
 */

import { baseApi } from "./baseApi";

/**
 * Notification type enum
 */
export type NotificationType =
	| "payment_due"
	| "payment_received"
	| "maintenance_request"
	| "maintenance_completed"
	| "lease_expiring"
	| "message"
	| "system"
	| "other";

/**
 * Notification interface
 */
export interface Notification {
	id: string;
	type: NotificationType;
	title: string;
	message: string;
	link?: string;
	is_read: boolean;
	read_at?: string;
	created_at: string;
}

/**
 * Mark all read response
 */
export interface MarkAllReadResponse {
	message: string;
}

/**
 * Notifications API endpoints
 */
export const notificationsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List user's notifications
		 */
		listNotifications: builder.query<Notification[], { unread_only?: boolean }>(
			{
				query: (params = {}) => {
					const queryParams = new URLSearchParams();
					if (params.unread_only) {
						queryParams.append("unread_only", "true");
					}
					const queryString = queryParams.toString();
					return {
						url: `/notifications/${queryString ? `?${queryString}` : ""}`,
						method: "GET",
					};
				},
				transformResponse: (response: any) => {
					// Backend returns {count, results}, but frontend expects array
					if (Array.isArray(response)) {
						// If response is already an array, return it
						return response;
					}
					if (response && response.results && Array.isArray(response.results)) {
						return response.results;
					}
					// Fallback to empty array if structure is unexpected
					console.warn(
						"Unexpected notifications response structure:",
						response,
					);
					return [];
				},
				providesTags: ["Notifications"],
			},
		),

		/**
		 * Get notification by ID
		 */
		getNotification: builder.query<Notification, string>({
			query: (id) => ({
				url: `/notifications/${id}/`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "Notifications", id }],
		}),

		/**
		 * Mark notification as read
		 */
		markNotificationRead: builder.mutation<Notification, string>({
			query: (id) => ({
				url: `/notifications/${id}/mark_read/`,
				method: "POST",
			}),
			invalidatesTags: (result, error, id) => [
				{ type: "Notifications", id },
				{ type: "Notifications", id: "LIST" },
			],
		}),

		/**
		 * Mark all notifications as read
		 */
		markAllNotificationsRead: builder.mutation<MarkAllReadResponse, void>({
			query: () => ({
				url: "/notifications/mark_all_read/",
				method: "POST",
			}),
			invalidatesTags: [{ type: "Notifications", id: "LIST" }],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListNotificationsQuery,
	useLazyListNotificationsQuery,
	useGetNotificationQuery,
	useMarkNotificationReadMutation,
	useMarkAllNotificationsReadMutation,
} = notificationsApi;
